import { useEffect } from "react";
import { useLocation } from "react-router";
import { useAppDispatch, useAppSelector } from "@/common/hooks/useRedux";
import { useAccountContext } from "@/state/providers/AccountProvider";
import { useImpersonationAwareUser } from "@/common/hooks/useImpersonationAwareUser";
import { clearPrefetchedPatientRecords } from "@/features/patient-view/state/patientRecordLoad";
import { REVIEW_SUMMARY_PATH } from "./paths";
import { endReview, markReviewEntered, selectReviewSession } from "./state/reviewSessionSlice";

const APPOINTMENT_PATH = /^\/appointments\/([^/]+)\/?$/;

// A malformed escape (e.g. "%E0") makes decodeURIComponent throw; such a path is not a queued visit.
const decodeVisitId = (raw: string): string | undefined => {
    try {
        return decodeURIComponent(raw);
    } catch {
        return undefined;
    }
};

// EN-18 Review Mode: ends the session when the user leaves the review flow or switches clinic.
const ReviewSessionGuard = () => {
    const dispatch = useAppDispatch();
    const { pathname } = useLocation();
    const { active, entered, records, accountId, userId, startPath } = useAppSelector(selectReviewSession);
    const currentAccountId = useAccountContext().accountData?.id;
    const { adminUserId: currentUserId } = useImpersonationAwareUser();

    useEffect(() => {
        if (!active) return;
        const rawId = APPOINTMENT_PATH.exec(pathname)?.[1];
        const visitId = rawId === undefined ? undefined : decodeVisitId(rawId);
        const inFlow =
            pathname === REVIEW_SUMMARY_PATH || (visitId !== undefined && records.some((r) => r.id === visitId));
        // An undefined account (e.g. a provider refetch) is not a clinic change.
        const clinicChanged = currentAccountId !== undefined && currentAccountId !== accountId;
        // A review restored after a refresh can outlive a sign-out in this tab; it never carries over to someone else.
        // Neither check is debounced. A transient unknown (account refetching, session not yet read) is ignored above;
        // a different defined clinic or user is a real change, and the review must not outlive it. Ending mid-move is
        // safe: the move re-checks the session after its save and its write, and drops the write's navigation.
        const userChanged = currentUserId !== null && currentUserId !== userId;
        // The session starts on the list, before navigation to the first visit lands: the start route is not
        // leaving. Any other route outside the flow is, whether or not the opening navigation ever landed.
        const left = !inFlow && (entered || pathname !== startPath);
        if (clinicChanged || userChanged) dispatch(endReview());
        else if (inFlow && !entered) dispatch(markReviewEntered());
        else if (left) dispatch(endReview());
    }, [accountId, active, currentAccountId, currentUserId, dispatch, entered, pathname, records, startPath, userId]);

    // A prefetched visit belongs to this review and this clinic; none may outlive the session.
    useEffect(() => {
        if (!active) clearPrefetchedPatientRecords();
    }, [active]);

    return null;
};

export default ReviewSessionGuard;
